import React, { useState, useMemo, useEffect } from 'react';
import { IUserJson } from '@scale/shared/types/user';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import {
  Typography,
  RadioCardGroup,
  SelectMenu,
  TextField,
  Autocomplete,
  Button,
  BaseIcon,
  Icon,
  Checkbox,
  Tooltip,
  FileDropzone,
  ConfirmDialog,
} from '@scale/scaleui';
import CampaignSetupCard from './CampaignSetupCard';
import {
  PodGroup,
  EmailDripCampaignType,
  EmailDripCampaignCondition,
  EmailDrip,
  GreenhouseJob,
  EmailType,
  NewCampaignRequest,
  DripRequest,
  CommsType,
  ALLOWED_AUDIENCE_SOURCE_TYPES,
  ISignupFlow,
  EmailCommsTemplateStatus,
} from '@scale/shared-types';
import {
  findOCPWorkflows,
  findWorkerTeamNameById,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';
import { IOnboardingFlow } from '@scale/shared/onboardingControlPanel';
import DripSetup from './DripSetup';
import useDrips from './useDrips';
import EmailTemplateModalV2 from './EmailTemplateModalV2';
import UpdateCSVCampaignModal from './UpdateCSVCampaignModal';
import {
  createEmailCampaignWithEmails,
  getJobStages,
  getEmailDripCampaign,
  getEmailDripsByCampaignId,
  updateEmailCampaignWithEmails,
  getAllGreenhouseJobs,
  getCSVFileHeaderAndFirstRow,
  findProjectNameById,
  transformCSVUrl,
  getCampaignByNameIfExists,
  getEmailCommsTemplateVersionStatuses,
} from 'react-scale/corp/lib/api/growth';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import {
  ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY,
  YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY,
} from 'react-scale/components/GrowthCenter/constants';
import SAlert from 'react-s-alert';
import { HTTPError } from 'react-scale/lib/api-fetch';
import parse from 'csv-parse/lib/sync';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { WorkerTeam as IWorkerTeam } from '@scale/shared/workerTeams/types';
import { ITagJson } from '@scale/shared/types/tag';
import { findACPWorkflows } from 'react-scale/corp/components/QM/training/api';
import SelectWorkerTeam from 'react-scale/corp/components/UI/SelectWorkerTeam';
import { SelectOption } from 'react-scale/corp/components/UI/Select';
import SelectProject from 'react-scale/corp/components/UI/SelectProject';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import isemail from 'isemail';
import { maybeCleanOutlierEmail } from '@scale/shared/email';
import SelectOptions from 'react-scale/components/GrowthCenter/components/SelectOptions';
import { downloadCSV } from 'react-scale/components/GrowthCenter/EmailDripCampaign/EmailDripCampaignTable';
import { useDebounce } from 'react-use-v13';

const OCP_URL = 'https://dashboard.scale.com/corp/growth-center/onboarding-control-panel';

// eslint-disable-next-line complexity
const CampaignSetupPage: React.FC<{ user: IUserJson }> = ({ user }) => {
  const { campaignId } = useParams<{ campaignId: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isDuplicate = queryParams.has('duplicate');

  const [campaignDetail, setCampaignDetail] = useState<NewCampaignRequest>({
    createdBy: user._id,
  });

  useEffect(() => {
    async function fetchCampaign() {
      if (campaignId) {
        const campaign = await getEmailDripCampaign(campaignId);
        if (isDuplicate) {
          SAlert.info(
            `This is a duplicate of the communication titled "${campaign.name}". Please update the details and save to activate this new communication.`,
            {
              timeout: 10000,
            },
          );
          const copyName = queryParams.get('duplicate');
          setCampaignDetail({
            ...campaign,
            createdBy: user._id,
            csvFile: undefined,
            csvCDSUrl: undefined,
            _id: undefined,
            name: copyName,
          } as NewCampaignRequest);
        } else {
          setCampaignDetail(campaign as NewCampaignRequest);
        }
        // these fields are not part of campaignDetail, so need to set them separately for them to show up on the UI
        if (campaign.sendAt) {
          const sendAtDate = new Date(campaign.sendAt);
          setIsSchedule(true);
          handleSetHour(convert24To12(sendAtDate.getHours()).hour);
          handleSetMinute(sendAtDate.getMinutes());
          setIsAM(convert24To12(sendAtDate.getHours()).isAM);
          setViewOnlyScheduleTime(sendAtDate);
        }

        if (campaign.onboardingFlowId && campaign.type === EmailDripCampaignType.OCP) {
          const flows = await findOCPWorkflows();
          const flow = flows.find(flow => flow._id === campaign.onboardingFlowId);
          setOcpFlowName(flow?.name.toString() ?? '');
          const steps = flow?.nodes.map(node => node.name);
          if (steps) {
            setOcpStepOptions(steps);
          }
        } else if (campaign.onboardingFlowId && campaign.type === EmailDripCampaignType.ACP) {
          const flows = await findACPWorkflows(campaign.onboardingFlowId.toString());
          setAcpFlowName(
            flows.find(flow => flow._id === campaign.onboardingFlowId)?.name.toString() ?? '',
          );
        }

        if ((campaign as NewCampaignRequest).csvCDSUrl) {
          try {
            const firstRow = await getCSVFileHeaderAndFirstRow(campaign);
            setCsvSubstitutions(firstRow);
          } catch (e) {
            SAlert.error('Error fetching CSV file header. Please check the file.', {
              timeout: 2000,
            });
          }
        }

        const drips = await getEmailDripsByCampaignId(campaignId);
        setConditions(drips[0]?.conditions ?? []);
        const updatedDrips = drips.map((drip: EmailDrip) => {
          const updatedDrip = {
            ...drip,
            tested: true,
          };
          return updatedDrip as DripRequest;
        });
        if (isDuplicate) {
          // check if the templates are still public
          const { templateToVersionAndStatus } = await getEmailCommsTemplateVersionStatuses(
            drips
              .filter(drip => drip.templateId)
              .map(drip => ({
                templateId: drip.templateId!,
                version: drip.templateVersion || 0,
              })),
          );
          const filteredDrips = [];
          const resetDrips = [];
          for (const drip of updatedDrips) {
            if (
              drip.templateId &&
              templateToVersionAndStatus[drip.templateId][drip.templateVersion || 0] ===
                EmailCommsTemplateStatus.Public
            ) {
              filteredDrips.push({ ...drip, _id: undefined });
            } else {
              resetDrips.push(drip.name);
            }
          }
          if (resetDrips.length) {
            SAlert.info(
              `Removed drips with templates that are no longer public: ${resetDrips.join(', ')}`,
              {
                timeout: 10000,
              },
            );
          }
          dripsHook.setDrips(filteredDrips);
        } else {
          dripsHook.setDrips(updatedDrips);
        }
      }
    }
    fetchCampaign();
  }, [campaignId]);

  const isUpdate = useMemo(
    () => !!campaignId && !isDuplicate && campaignDetail.commsType !== CommsType.Broadcast,
    [campaignId, isDuplicate, campaignDetail.commsType],
  );
  const isViewOnly = useMemo(
    () => !!campaignId && campaignDetail.commsType === CommsType.Broadcast && !isDuplicate,
    [campaignId, campaignDetail.commsType, isDuplicate],
  );

  const dripsHook = useDrips([]);

  const [ocpFlowName, setOcpFlowName] = useState<string>('');

  const [ocpFlowOptions, setOcpFlowOptions] = useState<IOnboardingFlow[]>([]);

  const [ocpStepOptions, setOcpStepOptions] = useState<string[]>([]);

  const [acpFlowName, setAcpFlowName] = useState<string>('');

  const [acpFlowOptions, setAcpFlowOptions] = useState<IOnboardingFlow[]>([]);
  const [acpSearchTerm, setAcpSearchTerm] = useState<string>('');

  const [acpStepOptions, setAcpStepOptions] = useState<string[]>([]);

  const [isEmailTemplateModalOpen, setIsEmailTemplateModalOpen] = useState<boolean>(false);

  const [selectedDripIndex, setSelectedDripIndex] = useState<number | undefined>();

  const [greenhouseStageNameOptions, setGreenhouseStageNameOptions] = useState<string[]>([]);

  const [conditions, setConditions] = useState<EmailDripCampaignCondition[]>([]);

  const [greenhouseJobs, setGreenhouseJobs] = useState<GreenhouseJob[]>([]);

  const [loadingJobs, setLoadingJobs] = useState<boolean>(false);

  const [validName, setValidName] = useState<boolean>(false);
  const [validDrips, setValidDrips] = useState<boolean>(false);
  const [dripMsg, setDripMsg] = useState<string>('');
  const [validTriggers, setValidTriggers] = useState<boolean>(false);
  const [triggerMsg, setTriggerMsg] = useState<string>('');

  const [csvSubstitutions, setCsvSubstitutions] = useState<Record<string, string>>({});
  const [showUpdateCSVModal, setShowUpdateCSVModal] = useState<boolean>(false);

  const [signupFlowErrorMsg, setSignupFlowErrorMsg] = useState<string>('');

  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState<boolean>(false);

  const [isSchedule, setIsSchedule] = useState<boolean>(false);
  const [scheduleHour, setScheduleHour] = useState<number | undefined>(undefined);
  const [scheduleMinute, setScheduleMinute] = useState<number | undefined>(undefined);
  const [isAM, setIsAM] = useState<boolean>(true);
  const [viewOnlyScheduleTime, setViewOnlyScheduleTime] = useState<Date | undefined>(undefined);

  const signupFlowQuery = useQuery(
    [campaignDetail.signupFlowId],
    async () => {
      const signupFlow = await apiFetch<{
        signupFlow: ISignupFlow;
        emails: string[];
        workerTeam: IWorkerTeam;
        tags: ITagJson[];
        userCount: number;
      }>(null, `/corp-api/onboarding-control-panel/signup-flow/${campaignDetail.signupFlowId}`);
      return signupFlow;
    },
    {
      enabled:
        !!campaignDetail.signupFlowId &&
        campaignDetail.greenhouseStageNames &&
        campaignDetail.greenhouseStageNames.length > 0 &&
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          campaignDetail.signupFlowId,
        ),
    },
  );

  const [commsSourceOptions, setCommsSourceOptions] = useState<string[]>([]);

  const [selectedWorkerTeams, setSelectedWorkerTeams] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);

  const [selectedWorkerTeam, setSelectedWorkerTeam] = useState<SelectOption | null>(null);

  useEffect(() => {
    if (!campaignDetail.commsType) {
      return;
    }
    setCommsSourceOptions(ALLOWED_AUDIENCE_SOURCE_TYPES[campaignDetail.commsType as CommsType]);
    if (campaignDetail._id) {
      return;
    }
    setOcpFlowName('');
    setAcpFlowName('');
    updateCampaignField({
      greenhouseJobId: undefined,
      greenhouseJobName: undefined,
      greenhouseStageNames: [],
      signupFlowId: undefined,
      csvFile: undefined,
      type: undefined,
    });
    setCsvSubstitutions({});
    setSelectedWorkerTeams([]);
    setSelectedWorkerTeam(null);
    setSelectedProjects([]);
    setIsSchedule(false);
    handleSetHour(undefined);
    handleSetMinute(undefined);
    setIsAM(true);
  }, [campaignDetail.commsType]);

  const toggleNoOutlierAccountCondition = () => {
    if (conditions.includes(EmailDripCampaignCondition.noOutlierAccount)) {
      setConditions(
        conditions.filter(condition => condition !== EmailDripCampaignCondition.noOutlierAccount),
      );
    } else {
      setConditions([...conditions, EmailDripCampaignCondition.noOutlierAccount]);
    }
  };

  const toggleNoRemotasksAccountCondition = () => {
    if (conditions.includes(EmailDripCampaignCondition.noRemoAccount)) {
      setConditions(
        conditions.filter(condition => condition !== EmailDripCampaignCondition.noRemoAccount),
      );
    } else {
      setConditions([...conditions, EmailDripCampaignCondition.noRemoAccount]);
    }
  };

  useEffect(() => {
    if (!isUpdate) {
      updateCampaignField({ greenhouseStageNames: [] });
      setGreenhouseStageNameOptions([]);
    }
    if (!campaignDetail.greenhouseJobId) return;
    const getJobStagesHelper = async (jobId: string) => {
      try {
        const stagesForJob = await getJobStages(jobId);
        if (!stagesForJob) return;
        const stagesNames = Object.values(stagesForJob);
        setGreenhouseStageNameOptions(stagesNames);
      } catch (e) {
        SAlert.error('Error fetching job stages. Please check the job and try again.', {
          timeout: 2000,
        });
      }
    };

    getJobStagesHelper(campaignDetail.greenhouseJobId);
  }, [campaignDetail.greenhouseJobId]);

  const onEmailTemplateModalClose = () => {
    setIsEmailTemplateModalOpen(false);
  };

  const onEmailTemplateModalOpen = (index: number) => {
    setIsEmailTemplateModalOpen(true);
    setSelectedDripIndex(index);
  };

  const handleAddDrip = (type: EmailType) => {
    dripsHook.setDrips([...dripsHook.drips, { active: true, type }]);
  };

  const updateCampaignField = (partialCampaign: NewCampaignRequest) => {
    if (partialCampaign.podGroup === PodGroup.None) {
      window.confirm(
        'Are you sure you want to set the pod group to None? ' +
          'The unsubscribe link in the email footer will be set to default group [Outlier Notifications] or [Remotasks Task Progress]' +
          " based on the sender's email domain",
      ) && setCampaignDetail({ ...campaignDetail, ...partialCampaign });
      return;
    }
    setCampaignDetail({ ...campaignDetail, ...partialCampaign });
  };

  const history = useHistory();
  const isCSVCampaign = campaignDetail.type === EmailDripCampaignType.CSV;
  const isACPCampaign = campaignDetail.type === EmailDripCampaignType.ACP;
  const isBroadcast = campaignDetail.commsType === CommsType.Broadcast;
  const enableDripSetup = !isBroadcast && campaignDetail.type !== EmailDripCampaignType.WorkerTeam;

  const launchCampaign = async () => {
    await createEmailCampaignWithEmails(
      {
        ...campaignDetail,
        workerTeamIds: selectedWorkerTeams.map(team => team.value),
        projectIds: selectedProjects.map(project => project.value),
      },
      dripsHook.drips,
      conditions,
    );
  };

  const queryClient = useQueryClient();
  const launchCampaignMutation = useMutation(launchCampaign, {
    onSuccess: () => {
      const successMessage = isBroadcast
        ? 'Broadcast create and send started! A notification email with sending results will be sent to you in at most 2 hours.'
        : 'Campaign launched successfully!';
      SAlert.success(successMessage, {
        timeout: 10000,
      });
      queryClient.invalidateQueries([ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
      queryClient.invalidateQueries([YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
      history.push(`${Routes.Communication}`);
      setCampaignDetail({
        createdBy: user._id,
      });
    },
    onError: error => {
      if (error instanceof HTTPError) {
        SAlert.error(`Failed to launch campaign, error: ${error.response?.message}`, {
          timeout: 30000,
          html: true,
        });
        return;
      } else {
        SAlert.error(`Failed to launch campaign, error: ${error}`, {
          timeout: 30000,
        });
      }
    },
  });

  const updateCampaign = async () => {
    await updateEmailCampaignWithEmails(campaignDetail, dripsHook.drips, conditions);
  };

  const updateCampaignMutation = useMutation(updateCampaign, {
    onSuccess: () => {
      const successMessage = isBroadcast
        ? 'Broadcast update and send started! A notification email with sending results will be sent to you in at most 2 hours.'
        : 'Campaign updated successfully!';
      SAlert.success(successMessage, {
        timeout: 10000,
      });
      queryClient.invalidateQueries([ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
      queryClient.invalidateQueries([YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY]);
      history.push(`${Routes.Communication}`);
    },
    onError: error => {
      if (error instanceof HTTPError) {
        SAlert.error(`Failed to update campaign, error: ${error.response?.message}`, {
          timeout: 30000,
          html: true,
        });
        return;
      } else {
        SAlert.error(`Failed to update campaign, ${error}`);
      }
    },
  });

  const progress = useMemo(() => {
    const hasAdvancedDetails = () => {
      const hasOcpDetails =
        campaignDetail.onboardingFlowId &&
        (campaignDetail.onboardingStepName || campaignDetail.onboardingSteps?.length);
      const hasGreenhouseDetails =
        campaignDetail.greenhouseJobId &&
        campaignDetail.greenhouseStageNames &&
        campaignDetail.greenhouseStageNames?.length > 0;
      const hasSignupFlow =
        campaignDetail.signupFlowId && !signupFlowErrorMsg && !signupFlowQuery.isLoading;
      const hasOfferStage = campaignDetail.greenhouseStageNames?.some(stageName =>
        stageName.toLowerCase().includes('offer'),
      );
      const hasCsvFile =
        campaignDetail.csvFile && campaignDetail.csvFile.name && campaignDetail.csvFile.size > 0;

      return (
        hasOcpDetails ||
        (hasGreenhouseDetails && (hasSignupFlow || !hasOfferStage)) ||
        hasCsvFile ||
        campaignDetail.csvCDSUrl ||
        selectedWorkerTeams.length > 0 ||
        selectedProjects.length > 0
      );
    };

    const hasBasicDetails = () =>
      campaignDetail.commsType &&
      campaignDetail.podGroup &&
      campaignDetail.name &&
      campaignDetail.type;

    if (hasBasicDetails() && hasAdvancedDetails()) {
      return 2;
    }
    if (hasBasicDetails()) {
      return 1;
    }
    return 0;
  }, [
    campaignDetail,
    selectedWorkerTeams,
    signupFlowErrorMsg,
    signupFlowQuery.isLoading,
    selectedProjects,
  ]);

  useEffect(() => {
    if (isUpdate) return;
    const fetchWorkflows = async () => {
      const data = await findOCPWorkflows();
      setOcpFlowOptions(data);
    };
    const fetchGreenhouseJobs = async () => {
      setLoadingJobs(true);
      const jobs = await getAllGreenhouseJobs();
      setGreenhouseJobs(jobs);
      setLoadingJobs(false);
    };
    if (campaignDetail.type === EmailDripCampaignType.OCP) {
      fetchWorkflows();
      setAcpFlowName('');
      updateCampaignField({
        greenhouseJobId: undefined,
        greenhouseJobName: undefined,
        greenhouseStageNames: [],
        signupFlowId: undefined,
        csvFile: undefined,
      });
      setCsvSubstitutions({});
      setSelectedWorkerTeams([]);
      setSelectedProjects([]);
    } else if (campaignDetail.type === EmailDripCampaignType.Greenhouse) {
      if (greenhouseJobs.length === 0) {
        fetchGreenhouseJobs();
      }
      setOcpFlowName('');
      setAcpFlowName('');
      updateCampaignField({
        onboardingStepName: '',
        csvFile: undefined,
      });
      setSelectedProjects([]);
      setSelectedWorkerTeams([]);
      setCsvSubstitutions({});
    } else if (campaignDetail.type === EmailDripCampaignType.CSV) {
      setOcpFlowName('');
      setAcpFlowName('');
      updateCampaignField({
        greenhouseJobId: undefined,
        greenhouseJobName: undefined,
        greenhouseStageNames: [],
        signupFlowId: undefined,
        onboardingStepName: '',
        csvFile: undefined,
      });
      setSelectedProjects([]);
      setSelectedWorkerTeams([]);
    } else if (campaignDetail.type === EmailDripCampaignType.ACP) {
      setOcpFlowName('');
      updateCampaignField({
        greenhouseJobId: undefined,
        greenhouseJobName: undefined,
        greenhouseStageNames: [],
        csvFile: undefined,
      });
      setSelectedWorkerTeams([]);
      setSelectedProjects([]);
      setCsvSubstitutions({});
    } else if (campaignDetail.type === EmailDripCampaignType.WorkerTeam) {
      setOcpFlowName('');
      setAcpFlowName('');
      updateCampaignField({
        greenhouseJobId: undefined,
        greenhouseJobName: undefined,
        greenhouseStageNames: [],
        onboardingStepName: '',
        csvFile: undefined,
      });
      setSelectedProjects([]);
      setCsvSubstitutions({});
    } else if (campaignDetail.type === EmailDripCampaignType.Project) {
      setOcpFlowName('');
      setAcpFlowName('');
      updateCampaignField({
        greenhouseJobId: undefined,
        greenhouseJobName: undefined,
        greenhouseStageNames: [],
        onboardingStepName: '',
        csvFile: undefined,
      });
      setSelectedWorkerTeams([]);
      setCsvSubstitutions({});
    }
  }, [campaignDetail.type]);

  useEffect(() => {
    if (
      campaignDetail.greenhouseStageNames?.length &&
      !campaignDetail.greenhouseStageNames.some(stageName =>
        stageName.toLowerCase().includes('offer'),
      )
    ) {
      updateCampaignField({
        signupFlowId: undefined,
      });
    }
  }, [campaignDetail.greenhouseStageNames]);

  const validateCSV = (csvData: string): [boolean, string] => {
    const rows: string[][] = parse(csvData, {
      ltrim: true,
      rtrim: true,
      bom: true,
    });

    const headers = rows[0];
    if (new Set(headers).size !== headers.length) {
      return [false, 'CSV contains duplicate headers'];
    }
    if (headers.some(header => !header)) {
      return [false, 'CSV contains empty header(s)'];
    }
    if (headers.some(header => /[^a-zA-Z0-9_]/.test(header))) {
      return [false, 'CSV contains header(s) with spaces and/or non-alphanumeric characters'];
    }
    const emailIndex = headers.findIndex(header => header.toLowerCase() === 'email');
    if (emailIndex === -1) {
      return [false, 'CSV does not contain an email column'];
    }
    if (rows.length < 2) {
      return [false, 'CSV must have a header row and data row(s)'];
    }

    const firstRowObject = headers.reduce((obj: Record<string, string>, header, i) => {
      header = header.toLowerCase() === 'email' ? 'email' : header;
      obj[header] = rows[1][i];
      return obj;
    }, {});
    setCsvSubstitutions(firstRowObject);

    for (let i = 1; i < rows.length; i++) {
      const emailAddress = rows[i][emailIndex];
      if (
        !emailAddress ||
        !isemail.validate(maybeCleanOutlierEmail(emailAddress), { minDomainAtoms: 2 })
      ) {
        return [true, `Invalid or empty email found in CSV row ${i + 1}`];
      }
    }

    for (const row of rows) {
      if (Object.values(row).some(cell => !cell)) {
        return [true, 'CSV contains empty cell(s)'];
      }
    }
    return [true, ''];
  };

  const handleFileChange = (file?: File) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) {
          const csvData = e.target.result as string;
          const [validCSV, csvMsg] = validateCSV(csvData);
          if (!validCSV) {
            SAlert.error(`${csvMsg}. Please upload a different CSV.`, {
              timeout: 5000,
            });
            return;
          } else if (csvMsg) {
            SAlert.warning(`${csvMsg}. Consider using a different CSV.`, {
              timeout: 5000,
            });
          }
          updateCampaignField({ csvFile: file });
        }
      };
      reader.readAsText(file);
    }
  };

  const isValidSignupFlowId = (signupFlowId: string): boolean => {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(signupFlowId);
  };

  useEffect(() => {
    if (campaignDetail.signupFlowId && !isValidSignupFlowId(campaignDetail.signupFlowId)) {
      setSignupFlowErrorMsg('Invalid signup flow ID');
      return;
    }

    const signupFlow = signupFlowQuery.data?.signupFlow;
    if (signupFlow) {
      const { whitelistGreenhouseJobIds, whitelistGreenhouseJobStageNames, active } = signupFlow;
      const { greenhouseJobId, greenhouseStageNames } = campaignDetail;

      const whitelistedStages = [...(whitelistGreenhouseJobStageNames ?? [])];
      if (whitelistedStages.length === 0) {
        whitelistedStages.push('Offer'); // Default stage
      }

      const hasWhitelistedIds = whitelistGreenhouseJobIds?.length;
      const hasGreenhouseStages = greenhouseStageNames?.length;
      const jobIdsMatch = whitelistGreenhouseJobIds?.includes(greenhouseJobId ?? '');
      const stagesMatch = greenhouseStageNames?.every(stage => whitelistedStages.includes(stage));

      if (!hasWhitelistedIds) {
        setSignupFlowErrorMsg('Signup flow is missing Greenhouse job ID(s)');
      } else if (!active) {
        setSignupFlowErrorMsg('Signup flow is not active');
      } else if (!greenhouseJobId || !jobIdsMatch) {
        setSignupFlowErrorMsg('Signup flow does not match Greenhouse job');
      } else if (!hasGreenhouseStages) {
        setSignupFlowErrorMsg('Greenhouse job stages are required');
      } else if (!stagesMatch) {
        setSignupFlowErrorMsg('Signup flow does not match Greenhouse job stages');
      } else {
        setSignupFlowErrorMsg('');
      }
    } else if (signupFlowQuery.isError) {
      setSignupFlowErrorMsg('Error fetching signup flow');
    }
  }, [
    signupFlowQuery.data,
    signupFlowQuery.isError,
    campaignDetail.greenhouseJobId,
    campaignDetail.greenhouseStageNames,
    campaignDetail.signupFlowId,
  ]);

  useEffect(() => {
    if (isUpdate || !ocpFlowName) return;
    updateCampaignField({
      onboardingFlowId: ocpFlowOptions.find(flow => flow.name === ocpFlowName)?._id.toString(),
      onboardingStepName: '',
      onboardingSteps: [],
    });

    const steps = ocpFlowOptions
      .find(flow => flow.name === ocpFlowName)
      ?.nodes.map(node => node.name);
    if (steps) {
      setOcpStepOptions(steps);
    }
  }, [ocpFlowName]);

  useEffect(() => {
    if (isUpdate || !acpFlowName) return;
    updateCampaignField({
      onboardingFlowId: acpFlowOptions.find(flow => flow.name === acpFlowName)?._id.toString(),
      onboardingStepName: '',
    });

    const steps = acpFlowOptions
      .find(flow => flow.name === acpFlowName)
      ?.nodes.map(node => node.name);
    if (steps) {
      setAcpStepOptions(steps);
    }
  }, [acpFlowName]);

  const filteredAcpOptions = useMemo(() => {
    if (!acpSearchTerm) return [];
    return acpFlowOptions
      .map(flow => flow.name)
      .filter(name => name.toLowerCase().includes(acpSearchTerm.toLowerCase()))
      .slice(0, 50);
  }, [acpFlowOptions, acpSearchTerm]);

  useEffect(() => {
    const validationHelper = async () => {
      let [valid, msg] = await dripsHook.validateTriggers(
        campaignDetail,
        Object.keys(csvSubstitutions).length > 0 ? Object.keys(csvSubstitutions) : undefined,
      );
      setValidTriggers(valid);
      setTriggerMsg(msg);

      [valid, msg] = await dripsHook.validateDrips(
        campaignDetail.name,
        campaignDetail.signupFlowId,
        campaignDetail.greenhouseStageNames,
      );
      setValidDrips(valid);
      setDripMsg(msg);
    };
    validationHelper();
  }, [campaignDetail, dripsHook.drips, csvSubstitutions]);

  useDebounce(
    async () => {
      if (campaignDetail.name) {
        const fetchedCampaign = await getCampaignByNameIfExists(campaignDetail.name);
        setValidName(!fetchedCampaign || fetchedCampaign._id === campaignId);
      } else setValidName(false);
    },
    300,
    [campaignDetail.name],
  );

  useDebounce(
    async () => {
      if (acpSearchTerm) {
        const data = await findACPWorkflows(acpSearchTerm);
        setAcpFlowOptions(data);
      }
    },
    300,
    [acpSearchTerm],
  );

  useEffect(() => {
    const getSelectedWorkerTeams = async () => {
      if (!campaignDetail.workerTeamIds) return;
      const workerTeamNameAndIds = await Promise.all(
        campaignDetail.workerTeamIds.map(async id => {
          return { label: await findWorkerTeamNameById(id), value: id };
        }),
      );
      setSelectedWorkerTeams(workerTeamNameAndIds);
    };
    if (campaignDetail.workerTeamIds?.length) {
      getSelectedWorkerTeams();
    }
  }, [campaignDetail.workerTeamIds]);

  useEffect(() => {
    const getSelectedProjects = async () => {
      if (!campaignDetail.projectIds) return;
      const projectNamesAndIds = await Promise.all(
        campaignDetail.projectIds.map(async id => {
          return { label: await findProjectNameById(id), value: id };
        }),
      );
      setSelectedProjects(projectNamesAndIds);
    };
    if (campaignDetail.projectIds?.length) {
      getSelectedProjects();
    }
  }, [campaignDetail.projectIds]);

  const isProjectBroadcastingEnabled = useUnleashFeatureFlag(
    'enable-project-broadcasting',
    { userId: user!._id },
    false,
  );

  const handleClickDownload = async () => {
    if (!campaignDetail.csvCDSUrl) {
      SAlert.error('No CSV file found');
      return;
    }
    const url = await transformCSVUrl(campaignDetail.csvCDSUrl);
    await downloadCSV(url, campaignDetail.name ?? 'download');
  };

  const convert12To24 = (hour: number, isAM: boolean): number => {
    if (isAM) return hour === 12 ? 0 : hour;
    else return 12 + (hour % 12);
  };

  const convert24To12 = (hour: number): { hour: number; isAM: boolean } => {
    return { hour: hour % 12 === 0 ? 12 : hour % 12, isAM: hour < 12 };
  };

  const scheduleTime = useMemo(() => {
    if (scheduleHour === undefined || scheduleMinute === undefined) return undefined;

    const now = new Date();
    const tempScheduleTime = new Date(
      now.getFullYear(),
      now.getMonth(),
      // note that getDate() returns the LOCAL TIME date, not UTC date
      now.getDate(),
      convert12To24(scheduleHour!, isAM),
      scheduleMinute!,
    );
    return tempScheduleTime < now
      ? new Date(tempScheduleTime.setDate(now.getDate() + 1))
      : tempScheduleTime;
  }, [scheduleHour, scheduleMinute, isAM]);

  useEffect(() => {
    updateCampaignField({ sendAt: scheduleTime } as NewCampaignRequest);
  }, [scheduleTime]);

  const handleSetHour = (hour: number | undefined): void => {
    if (hour !== undefined && hour < 1) setScheduleHour(1);
    else if (hour !== undefined && hour > 12) setScheduleHour(12);
    else setScheduleHour(hour);
  };

  const handleSetMinute = (minute: number | undefined): void => {
    if (minute !== undefined && minute < 0) setScheduleMinute(0);
    else if (minute !== undefined && minute > 59) setScheduleMinute(59);
    else setScheduleMinute(minute);
  };

  const validScheduleTime = useMemo(() => {
    return !isSchedule || (scheduleHour !== undefined && scheduleMinute !== undefined);
  }, [isSchedule, scheduleHour, scheduleMinute]);

  const hasThirtyMinuteBuffer = (() => {
    const now = new Date();
    const timeInThirtyMinutes = now.getTime() + 30 * 60 * 1000;
    return !isSchedule || !validScheduleTime || scheduleTime!.getTime() >= timeInThirtyMinutes;
  })();

  const handleOpenSignupFlow = () => {
    const signupFlow = signupFlowQuery.data?.signupFlow;
    if (!signupFlow) return;

    const onboardingFlowId = signupFlow.onboardingFlow;

    window.open(`${OCP_URL}/${onboardingFlowId}`, '_blank');
  };

  return (
    <div className="flex flex-col items-center justify-between text-neutral-600">
      <div className="flex flex-col items-start gap-5">
        <div className="flex flex-col self-start pt-2">
          <Button
            className="w-fit bg-transparent shadow-none"
            variant="neutral"
            onClick={() => setCancelConfirmationOpen(true)}
          >
            <BaseIcon
              contrastText
              edge="start"
              icon={['fas', 'arrow-left']}
              variant="neutral"
              size="sm"
            />
            Exit
          </Button>
          <ConfirmDialog
            open={cancelConfirmationOpen}
            onConfirm={() => {
              setCancelConfirmationOpen(false);
              history.push(`${Routes.Communication}`);
            }}
            onCancel={() => setCancelConfirmationOpen(false)}
          >
            Are you sure you want to cancel? Any information entered on this page will be lost.
          </ConfirmDialog>
        </div>
        <div className="flex flex-col gap-2 text-xl ml-1 font-bold">
          {isViewOnly
            ? 'View Communication'
            : campaignDetail._id
            ? 'Edit Communication'
            : 'New Communication'}
        </div>
        <CampaignSetupCard>
          <div className="w-full">
            <div className="flex flex-row place-items-center">
              <Typography.Subtitle3>Pod</Typography.Subtitle3>
              <Tooltip
                side="right"
                icon={['fas', 'info-circle']}
                target={
                  <div>
                    <Icon icon={['fas', 'info-circle']} />{' '}
                  </div>
                }
                text="Pod information is used to determine the unsubscribe link in the email footer."
              />
            </div>

            <SelectMenu
              hasAdaptiveWidth={true}
              disabled={isUpdate || isViewOnly}
              className="w-full"
              label=""
              menuDirection="bottom"
              options={Object.values(PodGroup).map(pod => ({
                id: pod,
                name: pod.charAt(0).toUpperCase() + pod.slice(1),
              }))}
              // @ts-ignore undefined selectedOptionId is valid when no option is selected
              selectedOptionId={campaignDetail.podGroup}
              setSelected={value => {
                updateCampaignField({ podGroup: value as PodGroup });
              }}
            />
          </div>
          <div className="w-full">
            <Typography.Subtitle3 className="py-1">Communication Name</Typography.Subtitle3>
            <TextField
              className="w-full"
              id="campaignName"
              label=""
              value={campaignDetail.name ?? ''}
              onChange={e => updateCampaignField({ name: e.target.value })}
              disabled={isViewOnly}
            />
          </div>

          <div className="w-full">
            <Typography.Subtitle3 className="py-1">Communication Type</Typography.Subtitle3>
            <RadioCardGroup
              className="w-full"
              onSelect={id =>
                !isUpdate &&
                !isViewOnly &&
                updateCampaignField({
                  commsType: id as CommsType,
                  excludeEmptyQueue: id === CommsType.Broadcast,
                  excludeFraudWorkerTeams: id === CommsType.Broadcast,
                  excludeInactiveWorkerTeams: id === CommsType.Broadcast,
                  ensureAvailableMarketplaceProjects: id === CommsType.Broadcast,
                })
              }
              options={[
                {
                  description:
                    'Recurring messages (OCP, ACP, Greenhouse, and Worker Team entry comms and reminders)',
                  icon: 'redo-alt',
                  iconVariant: 'neutral',
                  id: CommsType.Campaign,
                  title: 'Campaign',
                },
                {
                  description:
                    'Messages sent once to specific audiences (CSV, Worker Team, Project)',
                  icon: 'bullhorn',
                  iconVariant: 'neutral',
                  id: CommsType.Broadcast,
                  title: 'Broadcast',
                },
              ]}
              selectedId={campaignDetail.commsType}
            />
          </div>

          <Typography.Subtitle3>Communication Audience Source</Typography.Subtitle3>
          <SelectMenu
            disabled={isUpdate || isViewOnly}
            className="w-full"
            label=""
            menuDirection="bottom"
            options={commsSourceOptions.map(source => ({
              id: source,
              name:
                source.length <= 5
                  ? source.toUpperCase()
                  : source.replace(/\b\w/g, (l: string) => l.toUpperCase()),
            }))}
            // @ts-ignore undefined selectedOptionId is valid when no option is selected
            selectedOptionId={campaignDetail.type}
            setSelected={value => updateCampaignField({ type: value as EmailDripCampaignType })}
          />
          {campaignDetail.commsType === CommsType.Broadcast && (
            <div className="w-full">
              <Typography.Subtitle3 className="mb-2">Options</Typography.Subtitle3>
              <div className="flex gap-4">
                <Checkbox
                  disabled={isViewOnly}
                  checked={campaignDetail.excludeEmptyQueue}
                  onChange={e => updateCampaignField({ excludeEmptyQueue: e.target.checked })}
                  label="Exclude Empty Queue"
                />
                <Checkbox
                  disabled={isViewOnly}
                  checked={campaignDetail.excludeFraudWorkerTeams}
                  onChange={e => updateCampaignField({ excludeFraudWorkerTeams: e.target.checked })}
                  label="Exclude Fraud Worker Teams"
                />
                <Checkbox
                  disabled={isViewOnly}
                  checked={campaignDetail.excludeInactiveWorkerTeams}
                  onChange={e =>
                    updateCampaignField({ excludeInactiveWorkerTeams: e.target.checked })
                  }
                  label="Exclude Inactive Worker Teams"
                />
                <Checkbox
                  // note that isUpdate is trivially false because update is not supported for broadcast
                  disabled={isViewOnly}
                  checked={isSchedule}
                  onChange={e => {
                    setIsSchedule(e.target.checked);
                    handleSetHour(undefined);
                    handleSetMinute(undefined);
                    setIsAM(true);
                  }}
                  label="Schedule Broadcast"
                />
              </div>
              <div className="flex gap-4 mt-2">
                <Checkbox
                  disabled={isViewOnly}
                  checked={campaignDetail.ensureAvailableMarketplaceProjects}
                  onChange={e =>
                    updateCampaignField({ ensureAvailableMarketplaceProjects: e.target.checked })
                  }
                  label="Ensure Marketplace Users Have Available Projects"
                />
              </div>
            </div>
          )}
        </CampaignSetupCard>
        {isSchedule /* isSchedule guarantees !isUpdate */ && (
          <CampaignSetupCard>
            <Typography.Subtitle3>Schedule Broadcast</Typography.Subtitle3>
            <Typography.Paragraph3>
              Enter a time in your local time zone. Broadcasts can be scheduled up to 24 hours in
              advance. The broadcast will automatically be sent the next time the specified HH:MM
              occurs. The scheduled time must be at least 30 minutes from now. Scheduled broadcasts
              can be cancelled until 30 minutes before the scheduled send time.
            </Typography.Paragraph3>
            <div className="flex gap-1">
              <TextField
                className="w-20"
                id="scheduleHour"
                placeholder="HH"
                value={scheduleHour ?? ''}
                onChange={e => {
                  handleSetHour(e.target.value ? Number(e.target.value) : undefined);
                }}
                disabled={isViewOnly}
                error={!isViewOnly && !hasThirtyMinuteBuffer}
                type="number"
              />
              <Typography.H3>:</Typography.H3>
              <TextField
                className="w-20"
                id="scheduleMinute"
                placeholder="MM"
                value={
                  scheduleMinute !== undefined ? scheduleMinute.toString().padStart(2, '0') : ''
                }
                onChange={e => {
                  handleSetMinute(e.target.value ? Number(e.target.value) : undefined);
                }}
                disabled={isViewOnly}
                error={!isViewOnly && !hasThirtyMinuteBuffer}
                type="number"
              />
              <SelectMenu
                disabled={isViewOnly}
                className="w-20"
                menuDirection="bottom"
                options={[
                  { id: 'AM', name: 'AM' },
                  { id: 'PM', name: 'PM' },
                ]}
                selectedOptionId={isAM ? 'AM' : 'PM'}
                setSelected={value => setIsAM(value === 'AM')}
                error={!isViewOnly && !hasThirtyMinuteBuffer}
              />
            </div>
            {validScheduleTime ? (
              isViewOnly && !!viewOnlyScheduleTime ? (
                <Typography.Paragraph3>
                  Broadcast was scheduled for{' '}
                  {viewOnlyScheduleTime.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}{' '}
                  on{' '}
                  {
                    [
                      'January',
                      'February',
                      'March',
                      'April',
                      'May',
                      'June',
                      'July',
                      'August',
                      'September',
                      'October',
                      'November',
                      'December',
                    ][viewOnlyScheduleTime.getMonth()]
                  }{' '}
                  {viewOnlyScheduleTime.getDate().toString()}, {viewOnlyScheduleTime.getFullYear()}.
                </Typography.Paragraph3>
              ) : !hasThirtyMinuteBuffer ? (
                <Typography.Paragraph3 className="text-danger-500">
                  Current time is{' '}
                  {new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}
                  . ERROR: scheduled broadcast must be at least 30 minutes from now.
                </Typography.Paragraph3>
              ) : (
                <Typography.Paragraph3>
                  Current time is{' '}
                  {new Date().toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}
                  . Scheduled broadcast will take place at{' '}
                  {scheduleTime?.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}{' '}
                  {scheduleTime?.getDate() === new Date().getDate()
                    ? 'today'
                    : 'on the following day'}
                  .
                </Typography.Paragraph3>
              )
            ) : (
              <Typography.Paragraph3>
                Current time is{' '}
                {new Date().toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  timeZoneName: 'short',
                })}
                .
              </Typography.Paragraph3>
            )}
          </CampaignSetupCard>
        )}
        {progress >= 1 && campaignDetail.type === EmailDripCampaignType.OCP && (
          <CampaignSetupCard>
            <Autocomplete
              disabled={isUpdate || isViewOnly}
              canCreateOption={false}
              className="w-full"
              label="Onboarding Funnel Name"
              options={ocpFlowOptions.map(flow => flow.name)}
              value={ocpFlowName}
              setSelectedOption={setOcpFlowName}
            />

            <Typography.Subtitle3 className="py-0">Step Names</Typography.Subtitle3>
            <SelectOptions
              className="w-full"
              value={
                campaignDetail.onboardingSteps?.map(step => {
                  return { label: step.name, value: step.name };
                }) ?? []
              }
              options={ocpStepOptions.map(step => {
                return { label: step, value: step };
              })}
              onChange={(values: { label: string; value: string }[]) => {
                updateCampaignField({
                  onboardingSteps: values.map(value => ({ name: value.value })),
                });
              }}
              placeholder="Select OCP Steps"
              multi={true}
            />
          </CampaignSetupCard>
        )}
        {progress >= 1 && campaignDetail.type === EmailDripCampaignType.ACP && (
          <CampaignSetupCard>
            <Autocomplete
              disabled={!!isUpdate}
              canCreateOption={false}
              className="w-full"
              label="Insert Project ID (& select Attempt level)"
              options={filteredAcpOptions}
              value={acpFlowName}
              setSelectedOption={setAcpFlowName}
              onInputChange={setAcpSearchTerm}
              optionBoxClassName="max-h-[300px] overflow-y-auto"
            />

            <Autocomplete
              disabled={!!isUpdate}
              canCreateOption={false}
              className="w-full"
              label="Step Name"
              options={acpStepOptions}
              value={campaignDetail.onboardingStepName ?? ''}
              setSelectedOption={value => updateCampaignField({ onboardingStepName: value })}
            />
          </CampaignSetupCard>
        )}

        {progress >= 1 && campaignDetail.type === EmailDripCampaignType.WorkerTeam && (
          <CampaignSetupCard>
            <Typography.Subtitle3>Worker Teams</Typography.Subtitle3>

            {campaignDetail.commsType === CommsType.Broadcast ? (
              <Typography.Paragraph3>
                We will attempt to retrieve workers who are part of the selected worker teams.
              </Typography.Paragraph3>
            ) : (
              <Typography.Paragraph3>
                Contributors will receive this comm when they get added to this worker team!{' '}
                <b>
                  Communications will be delivered through both email and in-product notifications.
                  For in-product notifications, any variable substitutions will be represented as an
                  empty string.
                </b>{' '}
              </Typography.Paragraph3>
            )}

            <SelectWorkerTeam
              disabled={isUpdate || isViewOnly}
              className="w-full rounded-lg"
              placeholder="Select Worker Teams By Name"
              value={
                campaignDetail.commsType === CommsType.Broadcast
                  ? selectedWorkerTeams
                  : selectedWorkerTeam ?? selectedWorkerTeams[0]
              }
              onChange={(e: any) => {
                if (campaignDetail.commsType === CommsType.Broadcast) {
                  setSelectedWorkerTeams(e);
                } else {
                  setSelectedWorkerTeam(e);
                  setSelectedWorkerTeams([e]);
                }
              }}
              multi={campaignDetail.commsType === CommsType.Broadcast}
              closeOnSelect={campaignDetail.commsType !== CommsType.Broadcast}
              onSelectResetsInput={campaignDetail.commsType !== CommsType.Broadcast}
            />
          </CampaignSetupCard>
        )}

        {isProjectBroadcastingEnabled &&
          progress >= 1 &&
          campaignDetail.type === EmailDripCampaignType.Project && (
            <CampaignSetupCard>
              <Typography.Subtitle3>Projects</Typography.Subtitle3>
              <Typography.Paragraph3>
                We will attempt to retrieve contributors who get assigned to the selected projects
                in their queue in the past 7 days and are not disabled.
              </Typography.Paragraph3>
              <SelectProject
                disabled={isUpdate || isViewOnly}
                includeTestProjects={false}
                width="862px"
                placeholder="Select Project by name"
                value={selectedProjects}
                onChange={setSelectedProjects}
                multi={true}
                closeOnSelect={false}
                onSelectResetsInput={false}
              />
            </CampaignSetupCard>
          )}

        {progress >= 1 &&
          !isProjectBroadcastingEnabled &&
          campaignDetail.type === EmailDripCampaignType.Project && (
            <CampaignSetupCard>
              <Typography.Subtitle3>Projects</Typography.Subtitle3>
              <Typography.Paragraph3>Coming soon!</Typography.Paragraph3>
            </CampaignSetupCard>
          )}

        {progress >= 1 && campaignDetail.type === EmailDripCampaignType.Greenhouse && (
          <CampaignSetupCard>
            <Typography.Subtitle3>Greenhouse Job Name</Typography.Subtitle3>
            <Autocomplete
              disabled={isUpdate || isViewOnly || loadingJobs}
              canCreateOption={false}
              className="w-full h-auto"
              options={greenhouseJobs ? greenhouseJobs.map((job: GreenhouseJob) => job.name) : []}
              value={loadingJobs ? 'Loading options...' : campaignDetail?.greenhouseJobName || ''}
              setSelectedOption={value => {
                const selectedJob = greenhouseJobs.find(job => job.name === value);
                if (selectedJob) {
                  updateCampaignField({
                    greenhouseJobId: selectedJob.id.toString(),
                    greenhouseJobName: selectedJob.name,
                  });
                }
              }}
            />
            <div className="w-full">
              <Typography.Subtitle3 className="py-1">Greenhouse Stages</Typography.Subtitle3>
              <SelectMenu
                disabled={isUpdate || isViewOnly}
                className="w-full"
                label=""
                menuDirection="bottom"
                multiple
                options={greenhouseStageNameOptions.map(stage => ({
                  id: stage,
                  name: stage,
                }))}
                selectedOptionId={campaignDetail.greenhouseStageNames || []}
                setSelected={(value: string[]) => {
                  updateCampaignField({ greenhouseStageNames: value });
                }}
              />
            </div>
            {campaignDetail.greenhouseJobId &&
              !!campaignDetail.greenhouseStageNames?.length &&
              campaignDetail.greenhouseStageNames.some(stageName =>
                stageName.toLowerCase().includes('offer'),
              ) && (
                <div className="w-full">
                  <div className="flex flex-row items-center">
                    <Typography.Subtitle3 className="py-1 mr-1">
                      Signup Flow ID
                    </Typography.Subtitle3>
                    <Icon icon="external-link-alt" size="sm" onClick={handleOpenSignupFlow} />
                  </div>
                  <TextField
                    disabled={isUpdate || isViewOnly}
                    className="w-full"
                    id="campaignName"
                    placeholder="ex. 8ad7cd91-0734-48d2-9758-77d802290130"
                    error={
                      !signupFlowQuery.isLoading &&
                      !!campaignDetail.signupFlowId &&
                      !!signupFlowErrorMsg
                    }
                    helperText={
                      signupFlowQuery.isLoading
                        ? 'Validating...'
                        : campaignDetail.signupFlowId
                        ? signupFlowErrorMsg
                        : ''
                    }
                    value={campaignDetail.signupFlowId ?? ''}
                    onChange={e => updateCampaignField({ signupFlowId: e.target.value })}
                    trailingIcon={signupFlowQuery.isLoading ? 'spinner' : undefined}
                  />
                </div>
              )}
            <Typography.Subtitle3 className="py-1">
              If candidate has not signed up on (for drip emails):{' '}
            </Typography.Subtitle3>
            <div className="flex flex-row gap-4 w-full items-center">
              <div className="p-3 flex w-full justify-between ml-auto items-center border border-neutral-300 rounded-lg">
                <Typography.Subtitle3>Outlier</Typography.Subtitle3>
                <Checkbox
                  label=""
                  checked={conditions.includes(EmailDripCampaignCondition.noOutlierAccount)}
                  onChange={toggleNoOutlierAccountCondition}
                />
              </div>
              <div className="p-3 flex w-full justify-between ml-auto items-center border border-neutral-300 rounded-lg">
                <Typography.Subtitle3>Remotasks</Typography.Subtitle3>
                <Checkbox
                  label=""
                  checked={conditions.includes(EmailDripCampaignCondition.noRemoAccount)}
                  onChange={toggleNoRemotasksAccountCondition}
                />
              </div>
            </div>
          </CampaignSetupCard>
        )}

        {progress >= 1 && isCSVCampaign && (
          <CampaignSetupCard>
            <Typography.Subtitle3>CSV File Input</Typography.Subtitle3>
            <Typography.Paragraph3>
              Column names are case-sensitive and there must be a column named 'email'. The first
              row must be the header row with a list of column names, and the next rows must be data
              rows. Refer to columns as {'{{exact column name}}'} in the email template. '+outlier'
              will be stripped from end of email addresses.
            </Typography.Paragraph3>
            {!isViewOnly ? (
              <div className="flex justify-center w-full">
                <FileDropzone
                  file={campaignDetail?.csvFile}
                  setFile={handleFileChange}
                  accept={{ 'text/csv': ['.csv'] }}
                  label={campaignDetail?.csvCDSUrl ? 'Replace existing CSV' : 'Upload CSV'}
                />
              </div>
            ) : (
              <Button
                className="button-upload"
                variant="secondary"
                onClick={() => {
                  handleClickDownload();
                }}
              >
                {'Download CSV'}
              </Button>
            )}
            {Object.keys(csvSubstitutions).length > 0 && (
              <>
                <Typography.Subtitle4>Detected Variables</Typography.Subtitle4>
                <Typography.Paragraph3>
                  {Object.keys(csvSubstitutions).map((key, index) => (
                    <>
                      {index > 0 && ' '}
                      <span
                        key={index}
                        style={{ background: '#D1D5DB' }}
                        className="rounded p-1 mb-1 inline-block"
                      >
                        {`${key}`}
                      </span>
                    </>
                  ))}
                </Typography.Paragraph3>
              </>
            )}
          </CampaignSetupCard>
        )}

        {progress >= 2 && (
          <CampaignSetupCard>
            <Typography.Subtitle3>
              {!isBroadcast ? 'Trigger Emails' : 'Emails'}
            </Typography.Subtitle3>
            <Typography.Paragraph3>
              {!isBroadcast
                ? 'Email candidates when they reach a stage or step or get added to a team. '
                : `Communications will be delivered through both email and in-product
                  notifications. For in-product notifications, any variable substitutions will be
                  represented as an empty string. If you set up multiple emails, 
                  the first one will be used for in product notifications. `}
              <i>
                A/B test different templates with a % distribution per template. Percentages must
                add up to 100.
              </i>{' '}
              Metrics are based on template names, so make them unique and avoid changing them
              frequently.
            </Typography.Paragraph3>
            {dripsHook.drips.map(
              (drip, index) =>
                !drip.deletedAt &&
                drip.type === EmailType.Trigger && (
                  <div key={index}>
                    <DripSetup
                      index={index}
                      dripsHook={dripsHook}
                      onEmailTemplateModalOpen={onEmailTemplateModalOpen}
                      // @ts-ignore campaignDetail.podGroup will not be undefined
                      podGroup={campaignDetail.podGroup}
                      emailType={EmailType.Trigger}
                      csvSubstitutions={isCSVCampaign ? csvSubstitutions : undefined}
                      isViewOnly={isViewOnly}
                    />
                  </div>
                ),
            )}
            {dripsHook.countDrips(EmailType.Trigger) < 3 && !isViewOnly && (
              <Button
                className="flex flex-row gap-2 items-center mt-3 border-0 shadow-none"
                size="lg"
                variant="transparent"
                onClick={() => handleAddDrip(EmailType.Trigger)}
              >
                <BaseIcon edge="start" icon={['fas', 'plus']} />
                {!isBroadcast ? 'Add Trigger' : 'Add Email'}
              </Button>
            )}
          </CampaignSetupCard>
        )}
        {progress >= 2 && enableDripSetup && (
          <CampaignSetupCard>
            <Typography.Subtitle3>Drip Emails</Typography.Subtitle3>
            {!isACPCampaign ? (
              <Typography.Paragraph3>
                Set conditions based on absolute number of days someone has been waiting in a step
                for. Testing emails are required before launching a campaign.
              </Typography.Paragraph3>
            ) : (
              <Typography.Paragraph3>
                Set conditions based on absolute number of days someone has been waiting in a step
                for AND they have been inactive on the platform. This is because users may be active
                in one flow but inactive in another depending on their queue. Testing emails are
                required before launching a campaign.
              </Typography.Paragraph3>
            )}
            {dripsHook.drips.map(
              (drip, index) =>
                !drip.deletedAt &&
                drip.type === EmailType.Drip && (
                  <div key={index}>
                    <DripSetup
                      index={index}
                      dripsHook={dripsHook}
                      onEmailTemplateModalOpen={onEmailTemplateModalOpen}
                      // @ts-ignore campaignDetail.podGroup will not be undefined
                      podGroup={campaignDetail.podGroup}
                      emailType={EmailType.Drip}
                      isViewOnly={isViewOnly}
                    />
                  </div>
                ),
            )}
            {dripsHook.countDrips(EmailType.Drip) < 5 && (
              <Button
                className="flex flex-row gap-2 items-center mt-3 border-0 shadow-none"
                size="lg"
                variant="transparent"
                onClick={() => handleAddDrip(EmailType.Drip)}
              >
                <BaseIcon edge="start" icon={['fas', 'plus']} />
                Add Drip
              </Button>
            )}
          </CampaignSetupCard>
        )}
        {!isUpdate && !isViewOnly && (
          <Typography.Paragraph3 className="flex flex-row gap-3 ml-auto pb-0 text-danger-500">
            {progress < 1
              ? 'Error: Missing campaign fields'
              : progress < 2
              ? 'Error: Missing OCP/Greenhouse/CSV/Worker Team/Project fields'
              : !validName
              ? `Error: Communication with name '${campaignDetail.name ?? ''}' already exists`
              : !validTriggers
              ? `Error: ${triggerMsg}`
              : !validDrips
              ? `Error: ${dripMsg}`
              : !validScheduleTime
              ? 'Error: Please enter a a valid schedule time'
              : !hasThirtyMinuteBuffer
              ? 'Error: Scheduled time must be at least 30 minutes from now'
              : ''}
          </Typography.Paragraph3>
        )}
        {isUpdate && !isBroadcast && (
          <Typography.Paragraph3 className="flex flex-row gap-3 ml-auto pb-0 text-danger-500">
            {!validName
              ? `Error: Communication with name '${campaignDetail.name ?? ''}' already exists`
              : !validTriggers
              ? `Error: ${triggerMsg}`
              : !validDrips
              ? `Error: ${dripMsg}`
              : ''}
          </Typography.Paragraph3>
        )}
        <div className="flex flex-row gap-3 ml-auto pb-8">
          <Button size="lg" variant="neutral" onClick={() => setCancelConfirmationOpen(true)}>
            Cancel
          </Button>
          <ConfirmDialog
            open={cancelConfirmationOpen}
            onConfirm={() => {
              setCancelConfirmationOpen(false);
              history.push(`${Routes.Communication}`);
            }}
            onCancel={() => setCancelConfirmationOpen(false)}
          >
            Are you sure you want to cancel? Any information entered on this page will be lost.
          </ConfirmDialog>
          {!isUpdate && !isViewOnly && (
            <Button
              size="lg"
              variant="primary"
              disabled={
                progress < 2 ||
                !validName ||
                !validDrips ||
                !validScheduleTime ||
                !hasThirtyMinuteBuffer ||
                !validTriggers ||
                launchCampaignMutation.isLoading
              }
              onClick={() => launchCampaignMutation.mutate()}
            >
              {!isBroadcast
                ? launchCampaignMutation.isLoading
                  ? 'Launching...'
                  : 'Launch Campaign'
                : isSchedule
                ? launchCampaignMutation.isLoading
                  ? 'Scheduling...'
                  : 'Schedule Broadcast'
                : launchCampaignMutation.isLoading
                ? 'Sending...'
                : 'Save and Send'}
            </Button>
          )}
          {isUpdate && !isBroadcast && (
            <Button
              size="lg"
              variant="primary"
              disabled={
                !validName || !validDrips || !validTriggers || updateCampaignMutation.isLoading
              }
              onClick={() => {
                if (isCSVCampaign) {
                  setShowUpdateCSVModal(true);
                } else {
                  updateCampaignMutation.mutate();
                }
              }}
            >
              {!isBroadcast
                ? updateCampaignMutation.isLoading
                  ? 'Updating...'
                  : 'Update Campaign'
                : updateCampaignMutation.isLoading
                ? 'Sending...'
                : 'Update and Send'}
            </Button>
          )}

          <UpdateCSVCampaignModal
            open={showUpdateCSVModal}
            onClose={() => setShowUpdateCSVModal(false)}
            onSubmit={() => {
              setShowUpdateCSVModal(false);
              updateCampaignMutation.mutate();
            }}
          />
        </div>
      </div>

      <div className="flex flex-col items-start gap-5">
        {selectedDripIndex !== undefined && (
          <EmailTemplateModalV2
            isOpen={isEmailTemplateModalOpen}
            dripsHook={dripsHook}
            index={selectedDripIndex}
            onClose={onEmailTemplateModalClose}
            userId={user._id.toString()}
            csvHeaders={Object.keys(csvSubstitutions)}
            isViewOnly={isViewOnly}
          />
        )}
      </div>
    </div>
  );
};

export default CampaignSetupPage;
